import { httpClient } from 'app/api/http/http-client';
import { fileBaseURL } from 'app/constants/enviroments';
import {
  AccountInfo,
  AniPayAddAlias,
  AniPayAddAliasResponse,
  AniPayAliasAction,
  AniPayAliasActionResponse,
  AniPayGetAliasResponse,
  AniPayGetCustomerType,
  AniPayGetInfo,
  AniPayGetPayment,
  AniPayGetPaymentIban,
  AniPayGetPaymentResponse,
  AniPayMakePayment,
  AniPayMakePaymentResponse,
  AniPayPhoneVerify,
  AniPayPhoneVerifyResponse,
  AniPayProcessResponse,
  AniPaySendOtp,
  AniPayUserInfoResponse,
  CreateAniPayTemplate,
  CustomerTypeResponse,
  TemplateResponse,
} from 'app/pages/anipay/model/types';
import useCurrencySymbol from 'app/utils/customHooks/useCurrencySymbol';

interface TransformedAccountInfo {
  value: string;
  label: string;
  currency: string;
  balance: number;
}

interface TransformedPaymentResponse extends Omit<AniPayGetPaymentResponse, 'accounts'> {
  accounts: TransformedAccountInfo[];
}

const anipayService = {
  uploadFileBaseUrl: fileBaseURL,
  anipayGetData: async (): Promise<AniPayProcessResponse> => {
    return await httpClient.get('/api-web/v1/main/ani-pay-page');
  },
  anipayGetUserInfo: async (data: AniPayGetInfo): Promise<AniPayUserInfoResponse> => {
    return await httpClient.post(
      `/api-web/v1/main/ani-pay-page?business_key=${data.business_key}`,
      {
        form_id: data.form_id,
      }
    );
  },
  anipayPhoneVerify: async (data: AniPayPhoneVerify): Promise<AniPayPhoneVerifyResponse> => {
    return await httpClient.post(
      `/api-web/v1/main/ani-pay-page?business_key=${data.business_key}`,
      {
        phone: data.phone,
      }
    );
  },
  anipaySendOtp: async (data: AniPaySendOtp): Promise<any> => {
    return await httpClient.post(
      `/api-web/v1/main/ani-pay-page?business_key=${data.business_key}`,
      { otp: data.otp }
    );
  },
  anipayGetAliases: async (): Promise<AniPayGetAliasResponse> => {
    return await httpClient.get(`/api-web/v1/main/add-alias`);
  },
  anipayAccountAction: async (data: AniPayAliasAction): Promise<AniPayAliasActionResponse> => {
    return await httpClient.post(
      `/api-web/v1/main/add-alias?business_key=${data.business_key}`,
      data
    );
  },
  anipayAddAlias: async (data: AniPayAddAlias): Promise<AniPayAddAliasResponse> => {
    return await httpClient.post(
      `/api-web/v1/main/add-alias?business_key=${data.business_key}`,
      data
    );
  },
  anipayAliasSendOtp: async (data: AniPaySendOtp): Promise<any> => {
    return await httpClient.post(`/api-web/v1/main/add-alias?business_key=${data.business_key}`, {
      otp: data.otp,
    });
  },
  anipayPaymentFlow: async (data: AniPayGetPayment): Promise<TransformedPaymentResponse> => {
    const response = await httpClient.post<AniPayGetPaymentResponse>(
      `/api-web/v1/main/ani_pay_payment?business_key=${data.business_key}`,
      {
        alias_type: data.aliasType,
        alias_value: data.aliasValue,
      }
    );
    const transformedAccounts = (response.accounts as unknown as AccountInfo[]).map(item => ({
      value: item.iban,
      label: `${item.emp_title} - ${item.main_balance} ${useCurrencySymbol(item.currency)}`,
      currency: item.currency,
      balance: item.main_balance,
    }));

    return {
      ...response,
      accounts: transformedAccounts,
    };
  },
  anipayPaymentFlowIban: async (
    data: AniPayGetPaymentIban
  ): Promise<TransformedPaymentResponse> => {
    const { business_key, ...rest } = data;
    const response = await httpClient.post<AniPayGetPaymentResponse>(
      `/api-web/v1/main/ani_pay_payment?business_key=${business_key}`,
      rest
    );
    const transformedAccounts = (response.accounts as unknown as AccountInfo[]).map(item => ({
      value: item.iban,
      label: `${item.emp_title} - ${item.main_balance} ${useCurrencySymbol(item.currency)}`,
      currency: item.currency,
      balance: item.main_balance,
    }));

    return {
      ...response,
      accounts: transformedAccounts,
    };
  },
  getCustomerType: async (): Promise<CustomerTypeResponse> => {
    const response = await httpClient.post<AniPayGetCustomerType>(
      `/api-web/v1/main/ani_pay_payment`
    );
    const customerType = response?.customer_type?.[0]?.customer_type?.map(
      (item: { id: string; title: string }) => ({
        value: item.id,
        label: item.title,
      })
    );

    return { customerType, business_key: response?.business_key };
  },
  anipayMakePayment: async (data: AniPayMakePayment): Promise<AniPayMakePaymentResponse> => {
    return await httpClient.post(
      `/api-web/v1/main/ani_pay_payment?business_key=${data.business_key}`,
      data
    );
  },
  anipaySignPayment: async (data: { form_id: string; business_key: string }): Promise<any> => {
    return await httpClient.post(
      `/api-web/v1/main/ani_pay_payment?business_key=${data.business_key}`,
      data
    );
  },
  anipayVerifyOtpPayment: async (data: AniPaySendOtp): Promise<any> => {
    return await httpClient.post(
      `/api-web/v1/main/ani_pay_payment?business_key=${data.business_key}`,
      data
    );
  },
  anipaySaveTemplate: async (data: CreateAniPayTemplate): Promise<any> => {
    return await httpClient.post(`/api-web/v1/main/save_template`, data);
  },
  anipayEditTemplate: async (data: CreateAniPayTemplate) => {
    return await httpClient.post('/api-web/v1/main/edit_template', data);
  },
  getAccessToken: async (): Promise<{ business_key: string; token: string }> => {
    return await httpClient.post(`/api-web/v1/main/get_my_token`);
  },
  // /find_template?id=69
  getAniPayTemplate: async (id: string): Promise<TemplateResponse> => {
    return await httpClient.get(`/api-web/v1/main/find_template?id=${id}`);
  },

  uploadAniPayOrderFile: async (
    data: FormData
  ): Promise<{
    file_name: string;
  }> => {
    const { token } = await anipayService.getAccessToken();
    const response: { data: { file_name: string } } = await httpClient.put(
      `/api/v2/uploader/corporate/payment/file`,
      data,
      {
        baseURL: anipayService.uploadFileBaseUrl,
        headers: {
          'Content-Type': 'multipart/form-data',
          Accept: '*/*',
          Authorization: token,
        },
      }
    );

    return response.data;
  },
};

export default anipayService;
